<!--
  OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
  all the essential functionalities required for any enterprise.
  Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com

  OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
  the GNU General Public License as published by the Free Software Foundation, either
  version 3 of the License, or (at your option) any later version.

  OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU General Public License for more details.

  You should have received a copy of the GNU General Public License along with OrangeHRM.
  If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="orangehrm-background-container">
    <div class="org-tree-container">
      <!-- Minimal Header Toolbar -->
      <header class="org-tree-header">
        <div class="org-tree-header-left">
          <oxd-text type="card-title">Organisation Chart</oxd-text>
          <div class="org-tree-stats">
            <span class="org-stat-tag">
              <strong>{{ totalEmployees }}</strong> Employees
            </span>
            <span class="org-stat-divider">·</span>
            <span class="org-stat-tag">
              <strong>{{ totalDepartments }}</strong> Units
            </span>
          </div>
        </div>

        <div class="org-tree-header-right">
          <!-- Minimal Zoom Controls -->
          <div class="org-zoom-controls">
            <button
              type="button"
              class="org-zoom-btn"
              title="Zoom out"
              aria-label="Zoom out"
              @click="zoomOut"
            >
              −
            </button>
            <button
              type="button"
              class="org-zoom-btn org-zoom-label"
              title="Reset zoom"
              aria-label="Reset zoom"
              @click="resetZoom"
            >
              {{ Math.round(zoomScale * 100) }}%
            </button>
            <button
              type="button"
              class="org-zoom-btn"
              title="Zoom in"
              aria-label="Zoom in"
              @click="zoomIn"
            >
              +
            </button>
            <button
              type="button"
              class="org-zoom-btn org-fit-btn"
              title="Fit to screen"
              aria-label="Fit to screen"
              @click="fitToScreen"
            >
              Fit
            </button>
          </div>
        </div>
      </header>

      <!-- Main Canvas Area -->
      <div
        ref="canvasRef"
        class="org-canvas-viewport"
        :class="{'is-panning': isPanning}"
        @mousedown="startPan"
        @mousemove="doPan"
        @mouseup="endPan"
        @mouseleave="endPan"
        @wheel.prevent="onWheel"
        @click="onCanvasClick"
      >
        <!-- Loading State -->
        <div v-if="isLoading" class="org-state-overlay">
          <div class="org-spinner" />
          <p class="org-state-text">Loading organisation chart…</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="isEmpty" class="org-state-overlay">
          <div class="org-empty-icon-wrap">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="36"
              height="36"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              viewBox="0 0 24 24"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <h2 class="org-empty-title">No Organisation Records Found</h2>
          <p class="org-state-text">
            Set up company units in Admin → Organization → Structure.
          </p>
        </div>

        <!-- SVG Tree Rendering -->
        <svg v-else class="org-svg-surface" width="100%" height="100%">
          <defs>
            <!-- Mermaid TD Crisp Arrow Marker -->
            <marker
              id="mermaid-arrow"
              viewBox="0 0 10 10"
              refX="6"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M 0 2 L 6 5 L 0 8 z" fill="#94a3b8" />
            </marker>
          </defs>

          <!-- Scaled and Panned Container -->
          <g :transform="`translate(${panX}, ${panY}) scale(${zoomScale})`">
            <!-- Connector Links (Mermaid TD Step Lines) -->
            <g class="org-links-layer">
              <path
                v-for="link in layoutLinks"
                :key="link.id"
                :d="link.path"
                class="org-tree-link"
                marker-end="url(#mermaid-arrow)"
              />
            </g>

            <!-- Tree Nodes Layer -->
            <g class="org-nodes-layer">
              <g
                v-for="node in layoutNodes"
                :key="node.data.id"
                :transform="`translate(${node.x}, ${node.y})`"
                class="org-node-group"
                :class="{
                  'is-department': node.data.type === 'department',
                  'is-employee': node.data.type === 'employee',
                  'is-selected':
                    selectedNode && selectedNode.id === node.data.id,
                }"
                @click.stop="selectNode(node.data)"
              >
                <!-- 1. DEPARTMENT CARD -->
                <template v-if="node.data.type === 'department'">
                  <!-- Card Rect (Centered at x=0, y=0) -->
                  <rect
                    x="-110"
                    y="-38"
                    width="220"
                    height="76"
                    rx="8"
                    ry="8"
                    class="org-card-bg"
                  />

                  <!-- Centered Unit Tag -->
                  <rect
                    x="-35"
                    y="-30"
                    width="70"
                    height="16"
                    rx="8"
                    class="org-tag-bg"
                  />
                  <text
                    x="0"
                    y="-22"
                    text-anchor="middle"
                    dominant-baseline="central"
                    class="org-tag-text"
                  >
                    UNIT
                  </text>

                  <!-- Centered Unit Name -->
                  <text
                    x="0"
                    y="-2"
                    text-anchor="middle"
                    dominant-baseline="central"
                    class="org-node-title"
                  >
                    {{ truncate(node.data.name, 24) }}
                  </text>

                  <!-- Centered Member Count -->
                  <text
                    x="0"
                    y="18"
                    text-anchor="middle"
                    dominant-baseline="central"
                    class="org-node-subtitle"
                  >
                    {{ node.data.employeeCount }}
                    {{ node.data.employeeCount === 1 ? 'member' : 'members' }}
                  </text>

                  <!-- Expand/Collapse Button (Centered at bottom edge: x=0, y=38) -->
                  <g
                    v-if="node.hasChildren"
                    class="org-expander-btn"
                    @click.stop="toggleCollapse(node.data, $event)"
                  >
                    <circle cx="0" cy="38" r="9" class="org-expander-circle" />
                    <text
                      x="0"
                      y="38"
                      text-anchor="middle"
                      dominant-baseline="central"
                      class="org-expander-symbol"
                    >
                      {{ node.isCollapsed ? '+' : '−' }}
                    </text>
                  </g>
                </template>

                <!-- 2. EMPLOYEE CARD -->
                <template v-else>
                  <!-- Card Rect (Centered at x=0, y=0) -->
                  <rect
                    x="-110"
                    y="-42"
                    width="220"
                    height="84"
                    rx="8"
                    ry="8"
                    class="org-card-bg"
                  />

                  <!-- Centered Avatar Circle with Initials -->
                  <circle cx="0" cy="-18" r="14" class="org-avatar-circle" />
                  <text
                    x="0"
                    y="-18"
                    text-anchor="middle"
                    dominant-baseline="central"
                    class="org-avatar-text"
                  >
                    {{ getInitials(node.data.name) }}
                  </text>

                  <!-- Centered Employee Name -->
                  <text
                    x="0"
                    y="7"
                    text-anchor="middle"
                    dominant-baseline="central"
                    class="org-node-title"
                  >
                    {{ truncate(node.data.name, 22) }}
                  </text>

                  <!-- Centered Job Title -->
                  <text
                    x="0"
                    y="24"
                    text-anchor="middle"
                    dominant-baseline="central"
                    class="org-node-subtitle"
                  >
                    {{
                      truncate(
                        node.data.title || node.data.subunitName || 'Employee',
                        24,
                      )
                    }}
                  </text>

                  <!-- Expand/Collapse Button (Centered at bottom edge: x=0, y=42) -->
                  <g
                    v-if="node.hasChildren"
                    class="org-expander-btn"
                    @click.stop="toggleCollapse(node.data, $event)"
                  >
                    <circle cx="0" cy="42" r="9" class="org-expander-circle" />
                    <text
                      x="0"
                      y="42"
                      text-anchor="middle"
                      dominant-baseline="central"
                      class="org-expander-symbol"
                    >
                      {{ node.isCollapsed ? '+' : '−' }}
                    </text>
                  </g>
                </template>
              </g>
            </g>
          </g>
        </svg>

        <!-- Slideout Details Drawer (Clean & Minimal) -->
        <aside v-if="selectedNode" class="org-details-drawer" @click.stop>
          <div class="orangehrm-modal-header org-drawer-header">
            <oxd-text type="card-title">
              {{
                selectedNode.type === 'department'
                  ? 'Unit Details'
                  : 'Employee Profile'
              }}
            </oxd-text>
            <button
              type="button"
              class="org-drawer-close"
              title="Close panel"
              aria-label="Close panel"
              @click="closeDrawer"
            >
              &times;
            </button>
          </div>
          <oxd-divider />

          <div class="org-drawer-body">
            <!-- DEPARTMENT DETAILS -->
            <div
              v-if="selectedNode.type === 'department'"
              class="org-drawer-content"
            >
              <div class="org-drawer-meta-card">
                <span class="org-meta-label">UNIT NAME</span>
                <p class="org-meta-value-title">{{ selectedNode.name }}</p>
                <div v-if="selectedNode.unitId" class="org-meta-code">
                  Code: {{ selectedNode.unitId }}
                </div>
                <div class="org-meta-count">
                  {{ selectedNode.employeeCount }}
                  {{
                    selectedNode.employeeCount === 1 ? 'employee' : 'employees'
                  }}
                  assigned
                </div>
              </div>

              <div
                v-if="selectedNode.employees && selectedNode.employees.length"
                class="org-drawer-section"
              >
                <h4 class="org-section-title">Members</h4>
                <div class="org-member-list">
                  <div
                    v-for="emp in flattenEmployees(selectedNode.employees)"
                    :key="emp.empNumber"
                    class="org-member-item"
                    @click="selectEmployeeFromList(emp)"
                  >
                    <div class="org-member-avatar">
                      {{ getInitials(emp.fullName) }}
                    </div>
                    <div class="org-member-info">
                      <div class="org-member-name">{{ emp.fullName }}</div>
                      <div class="org-member-role">
                        {{ emp.jobTitle || 'Team Member' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- EMPLOYEE DETAILS -->
            <div v-else class="org-drawer-content">
              <div class="org-drawer-profile-top">
                <div class="org-drawer-avatar-lg">
                  {{ getInitials(selectedNode.name) }}
                </div>
                <h4 class="org-drawer-emp-name">{{ selectedNode.name }}</h4>
                <p class="org-drawer-emp-title">{{ selectedNode.title }}</p>
                <span
                  v-if="selectedNode.subunitName"
                  class="org-drawer-unit-badge"
                >
                  {{ selectedNode.subunitName }}
                </span>
              </div>

              <div class="org-drawer-info-grid">
                <div v-if="selectedNode.employeeId" class="org-info-row">
                  <span class="org-info-label">Employee ID</span>
                  <span class="org-info-val">{{
                    selectedNode.employeeId
                  }}</span>
                </div>
                <div v-if="selectedNode.subunitName" class="org-info-row">
                  <span class="org-info-label">Unit</span>
                  <span class="org-info-val">{{
                    selectedNode.subunitName
                  }}</span>
                </div>
                <div v-if="selectedNode.supervisorName" class="org-info-row">
                  <span class="org-info-label">Reports To</span>
                  <span class="org-info-val">{{
                    selectedNode.supervisorName
                  }}</span>
                </div>
                <div v-if="selectedNode.reportingMethod" class="org-info-row">
                  <span class="org-info-label">Reporting Type</span>
                  <span class="org-info-val">{{
                    selectedNode.reportingMethod
                  }}</span>
                </div>
              </div>

              <!-- Direct Reports -->
              <div
                v-if="selectedNode.children && selectedNode.children.length"
                class="org-drawer-section"
              >
                <h4 class="org-section-title">
                  Direct Reports ({{ selectedNode.children.length }})
                </h4>
                <div class="org-member-list">
                  <div
                    v-for="report in selectedNode.children"
                    :key="report.id"
                    class="org-member-item"
                    @click="selectNode(report)"
                  >
                    <div class="org-member-avatar">
                      {{ getInitials(report.name) }}
                    </div>
                    <div class="org-member-info">
                      <div class="org-member-name">{{ report.name }}</div>
                      <div class="org-member-role">
                        {{ report.title || 'Team Member' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import {ref, computed, onMounted, nextTick} from 'vue';
import {hierarchy, tree} from 'd3-hierarchy';
import {APIService} from '@/core/util/services/api.service';

export default {
  name: 'OrgTreePage',

  setup() {
    const rawDepartments = ref([]);
    const rawEmployees = ref([]);
    const totalEmployees = ref(0);
    const totalDepartments = ref(0);
    const isLoading = ref(true);

    const selectedNode = ref(null);

    const canvasRef = ref(null);
    const panX = ref(0);
    const panY = ref(0);
    const zoomScale = ref(1.0);
    const isPanning = ref(false);

    let startMouseX = 0;
    let startMouseY = 0;
    let initialPanX = 0;
    let initialPanY = 0;

    const layoutNodes = ref([]);
    const layoutLinks = ref([]);

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/directory/org-tree',
    );

    function getInitials(name) {
      if (!name) return '??';
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    function truncate(str, maxLen = 22) {
      if (!str) return '';
      return str.length > maxLen ? str.slice(0, maxLen - 1) + '…' : str;
    }

    function flattenEmployees(empList) {
      const result = [];
      function recurse(list) {
        if (!list || !list.length) return;
        list.forEach((item) => {
          result.push(item);
          if (item.subordinates && item.subordinates.length) {
            recurse(item.subordinates);
          }
        });
      }
      recurse(empList);
      return result;
    }

    // Transform an employee and recursive subordinates
    function transformEmployee(emp) {
      const subordinates = (emp.subordinates || []).map(transformEmployee);
      const fullName =
        emp.fullName ||
        `${emp.firstName || ''} ${emp.lastName || ''}`.trim() ||
        'Employee';

      return {
        id: `emp-${emp.empNumber}`,
        rawId: emp.empNumber,
        type: 'employee',
        name: fullName,
        fullName,
        title: emp.jobTitle || 'Team Member',
        jobTitle: emp.jobTitle || 'Team Member',
        employeeId: emp.employeeId || '',
        subunitName: emp.subunitName || '',
        supervisorId: emp.supervisorId,
        supervisorName: emp.supervisorName || null,
        reportingMethod: emp.reportingMethod || 'Direct',
        _collapsed: false,
        children: subordinates,
      };
    }

    // Transform a unit and merge both sub-units and reporting employees
    function transformUnit(unit) {
      const subUnits = (unit.children || []).map(transformUnit);
      const unitEmployees = (unit.employees || []).map(transformEmployee);

      return {
        id: `unit-${unit.id}`,
        rawId: unit.id,
        type: 'department',
        name: unit.name || 'Unit',
        unitId: unit.unitId || '',
        employeeCount: unit.employeeCount || 0,
        employees: unit.employees || [],
        _collapsed: false,
        children: [...subUnits, ...unitEmployees],
      };
    }

    // Unified hierarchy root: merges units and employees into one cohesive tree
    const currentRootData = computed(() => {
      const depts = rawDepartments.value;
      if (!depts || !depts.length) {
        // Fallback: if only flat employees are returned
        if (rawEmployees.value && rawEmployees.value.length) {
          const empMap = new Map();
          rawEmployees.value.forEach((emp) => {
            const fullName =
              emp.fullName ||
              `${emp.firstName || ''} ${emp.lastName || ''}`.trim() ||
              'Employee';
            empMap.set(emp.empNumber, {
              id: `emp-${emp.empNumber}`,
              rawId: emp.empNumber,
              type: 'employee',
              name: fullName,
              fullName,
              title: emp.jobTitle || 'Team Member',
              jobTitle: emp.jobTitle || 'Team Member',
              employeeId: emp.employeeId || '',
              subunitName: emp.subunitName || '',
              supervisorId: emp.supervisorId,
              supervisorName: emp.supervisorName || null,
              reportingMethod: emp.reportingMethod || 'Direct',
              _collapsed: false,
              children: [],
            });
          });

          const roots = [];
          empMap.forEach((emp) => {
            if (emp.supervisorId && empMap.has(emp.supervisorId)) {
              empMap.get(emp.supervisorId).children.push(emp);
            } else {
              roots.push(emp);
            }
          });

          return {
            id: 'root-company',
            rawId: 0,
            type: 'department',
            name: 'Organisation',
            unitId: '',
            employeeCount: rawEmployees.value.length,
            employees: [],
            _collapsed: false,
            children: roots,
          };
        }
        return null;
      }

      if (depts.length === 1) {
        return transformUnit(depts[0]);
      }

      return {
        id: 'unit-root',
        rawId: 0,
        type: 'department',
        name: 'Organisation',
        unitId: '',
        employeeCount: depts.reduce(
          (acc, u) => acc + (u.employeeCount || 0),
          0,
        ),
        employees: [],
        _collapsed: false,
        children: depts.map(transformUnit),
      };
    });

    const isEmpty = computed(() => {
      return !rawDepartments.value.length && !rawEmployees.value.length;
    });

    // Run d3-hierarchy tree layout
    function computeLayout() {
      if (!currentRootData.value) {
        layoutNodes.value = [];
        layoutLinks.value = [];
        return;
      }

      const horizontalGap = 250;
      const verticalGap = 135;

      const root = hierarchy(currentRootData.value, (d) =>
        d._collapsed ? null : d.children,
      );

      const treeLayout = tree().nodeSize([horizontalGap, verticalGap]);
      treeLayout(root);

      layoutNodes.value = root.descendants().map((d) => ({
        x: d.x,
        y: d.y,
        depth: d.depth,
        data: d.data,
        hasChildren: Boolean(d.data.children && d.data.children.length > 0),
        isCollapsed: Boolean(d.data._collapsed),
        childrenCount: d.data.children ? d.data.children.length : 0,
      }));

      layoutLinks.value = root.links().map((link) => {
        const sourceHalfH = link.source.data.type === 'department' ? 38 : 42;
        const targetHalfH = link.target.data.type === 'department' ? 38 : 42;

        const sx = link.source.x;
        const sy = link.source.y + sourceHalfH;
        const tx = link.target.x;
        const ty = link.target.y - targetHalfH;
        const my = (sy + ty) / 2;

        // Smooth S-shaped cubic bezier connector path (prevents overlapping)
        const path = `M ${sx} ${sy} C ${sx} ${my}, ${tx} ${my}, ${tx} ${ty}`;
        return {
          id: `${link.source.data.id}->${link.target.data.id}`,
          path,
          source: link.source,
          target: link.target,
        };
      });
    }

    function fitToScreen() {
      if (!layoutNodes.value.length || !canvasRef.value) return;

      const rect = canvasRef.value.getBoundingClientRect();
      const containerW = rect.width || 1000;
      const containerH = rect.height || 600;

      const nodeHalfW = 110;
      const nodeHalfH = 42;

      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      layoutNodes.value.forEach((n) => {
        if (n.x - nodeHalfW < minX) minX = n.x - nodeHalfW;
        if (n.x + nodeHalfW > maxX) maxX = n.x + nodeHalfW;
        if (n.y - nodeHalfH < minY) minY = n.y - nodeHalfH;
        if (n.y + nodeHalfH > maxY) maxY = n.y + nodeHalfH;
      });

      const contentW = maxX - minX + 80;
      const contentH = maxY - minY + 100;

      const scaleX = containerW / contentW;
      const scaleY = containerH / contentH;
      const targetScale = Math.min(
        1.0,
        Math.max(0.35, Math.min(scaleX, scaleY) * 0.92),
      );

      zoomScale.value = Number(targetScale.toFixed(2));
      panX.value = Math.round(
        containerW / 2 - ((minX + maxX) / 2) * zoomScale.value,
      );
      panY.value = Math.round(50 - minY * zoomScale.value);
    }

    function toggleCollapse(nodeData, event) {
      if (event) event.stopPropagation();
      nodeData._collapsed = !nodeData._collapsed;
      computeLayout();
    }

    function selectNode(nodeData) {
      if (selectedNode.value && selectedNode.value.id === nodeData.id) {
        selectedNode.value = null;
      } else {
        selectedNode.value = nodeData;
      }
    }

    function selectEmployeeFromList(emp) {
      selectedNode.value = {
        id: `emp-${emp.empNumber}`,
        rawId: emp.empNumber,
        type: 'employee',
        name: emp.fullName,
        fullName: emp.fullName,
        title: emp.jobTitle || 'Team Member',
        jobTitle: emp.jobTitle || 'Team Member',
        employeeId: emp.employeeId || '',
        subunitName: emp.subunitName || '',
        supervisorId: emp.supervisorId,
        supervisorName: emp.supervisorName || null,
        reportingMethod: emp.reportingMethod || 'Direct',
        children: (emp.subordinates || []).map(transformEmployee),
      };
    }

    function closeDrawer() {
      selectedNode.value = null;
    }

    function onCanvasClick(e) {
      if (
        e.target.closest(
          '.org-node-group, .org-details-drawer, .org-tree-header',
        )
      ) {
        return;
      }
      selectedNode.value = null;
    }

    // Pan & Zoom handlers
    function startPan(e) {
      if (e.button !== 0) return;
      if (
        e.target.closest(
          '.org-expander-btn, .org-details-drawer, .org-tree-header',
        )
      ) {
        return;
      }
      isPanning.value = true;
      startMouseX = e.clientX;
      startMouseY = e.clientY;
      initialPanX = panX.value;
      initialPanY = panY.value;
    }

    function doPan(e) {
      if (!isPanning.value) return;
      panX.value = initialPanX + (e.clientX - startMouseX);
      panY.value = initialPanY + (e.clientY - startMouseY);
    }

    function endPan() {
      isPanning.value = false;
    }

    function onWheel(e) {
      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      const targetScale = Math.min(
        2.0,
        Math.max(0.3, zoomScale.value * zoomFactor),
      );

      if (!canvasRef.value) {
        zoomScale.value = Number(targetScale.toFixed(2));
        return;
      }

      const rect = canvasRef.value.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      panX.value = Math.round(
        mouseX - (mouseX - panX.value) * (targetScale / zoomScale.value),
      );
      panY.value = Math.round(
        mouseY - (mouseY - panY.value) * (targetScale / zoomScale.value),
      );
      zoomScale.value = Number(targetScale.toFixed(2));
    }

    function zoomIn() {
      zoomScale.value = Number(
        Math.min(2.0, zoomScale.value + 0.15).toFixed(2),
      );
    }

    function zoomOut() {
      zoomScale.value = Number(
        Math.max(0.3, zoomScale.value - 0.15).toFixed(2),
      );
    }

    function resetZoom() {
      zoomScale.value = 1.0;
      fitToScreen();
    }

    onMounted(async () => {
      try {
        const response = await http.getAll();
        const resData = response.data?.data;

        if (Array.isArray(resData)) {
          rawDepartments.value = resData;
          totalDepartments.value = resData.length;
        } else if (resData && typeof resData === 'object') {
          rawDepartments.value = resData.tree ?? resData.departments ?? [];
          rawEmployees.value = resData.allEmployees ?? [];
          totalEmployees.value =
            resData.totalEmployees ?? rawEmployees.value.length;
          totalDepartments.value =
            resData.totalDepartments ?? rawDepartments.value.length;
        }

        computeLayout();
        nextTick(() => {
          fitToScreen();
        });
      } catch {
        rawDepartments.value = [];
        rawEmployees.value = [];
      } finally {
        isLoading.value = false;
      }
    });

    return {
      rawDepartments,
      rawEmployees,
      totalEmployees,
      totalDepartments,
      isLoading,
      selectedNode,
      canvasRef,
      panX,
      panY,
      zoomScale,
      isPanning,
      layoutNodes,
      layoutLinks,
      isEmpty,
      getInitials,
      truncate,
      flattenEmployees,
      selectEmployeeFromList,
      toggleCollapse,
      selectNode,
      closeDrawer,
      onCanvasClick,
      startPan,
      doPan,
      endPan,
      onWheel,
      zoomIn,
      zoomOut,
      resetZoom,
      fitToScreen,
    };
  },
};
</script>

<style src="./org-tree.scss" lang="scss"></style>
